import { useContext } from 'react';

import { ProviderCallContext } from './ProviderCallContext';

/**
 * Hook to access provider call context.
 *
 * Must be used within ProviderCallContextController.
 *
 * Provides access to three logical groups of state:
 * - lifecycle: Appointment creation and participant tracking
 * - duration: Time calculation and manual editing
 * - ui: Modal state and user interactions
 *
 * @throws {Error} If used outside of ProviderCallContextController
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const { duration, lifecycle, ui } = useProviderCallContext();
 *
 *   // Access specific state groups
 *   const { finalDuration, calculateDuration } = duration;
 *   const { hasStartTime } = lifecycle;
 *   const { showTimeLimitModal, dismissTimeLimitModal } = ui;
 *
 *   // Or destructure directly
 *   const {
 *     duration: { finalDuration },
 *     lifecycle: { hasStartTime },
 *     ui: { showTimeLimitModal }
 *   } = useProviderCallContext();
 *
 *   return (
 *     <div>
 *       {hasStartTime && <p>Duration: {finalDuration} minutes</p>}
 *       {showTimeLimitModal && (
 *         <Modal onClose={dismissTimeLimitModal}>Time limit reached</Modal>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 *
 * @example Accessing lifecycle state
 * ```typescript
 * const { lifecycle } = useProviderCallContext();
 *
 * if (lifecycle.hasStartTime) {
 *   console.log('Call started at:', lifecycle.momentBothParticipantsJoined);
 *   console.log('Participant count:', lifecycle.participantCount);
 * }
 * ```
 *
 * @example Managing duration
 * ```typescript
 * const { duration } = useProviderCallContext();
 *
 * // Calculate duration when ending call
 * const handleEndCall = () => {
 *   duration.calculateDuration();
 *   console.log('Final duration:', duration.finalDuration);
 * };
 *
 * // Manual edit with reason
 * const handleEditDuration = () => {
 *   duration.editDuration(45, 'Technical Difficulties');
 * };
 * ```
 *
 * @example Managing UI state
 * ```typescript
 * const { ui } = useProviderCallContext();
 *
 * if (ui.showTimeLimitModal) {
 *   return <TimeLimitModal onClose={ui.dismissTimeLimitModal} />;
 * }
 * ```
 *
 * @see ProviderCallContextController - Provider component that must wrap your component tree
 */
export function useProviderCallContext() {
  const context = useContext(ProviderCallContext);

  if (!context) {
    throw new Error(
      'useProviderCallContext must be used within ProviderCallContextController. ' +
        'Make sure your component is rendered inside <ProviderCallContextController>.',
    );
  }

  return context;
}
